import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Activity } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ContextSyncIndicator — V23.15.22 (Doctrine 49 · transparence visuelle).
 *
 * Petit indicateur dans le topbar qui pulse brièvement quand Noam capture
 * une nouvelle modification. Tooltip décrit la dernière capture.
 * Clic → /app/settings/context-sync (journal détaillé).
 *
 * Mécanique :
 *  - Poll `/workspace/context-sync/status` toutes les 30s
 *  - Si `last_synced_at` a avancé depuis le dernier poll, on déclenche
 *    une animation pulse pendant 2.5s
 *  - Si la sync est désactivée (toggle global ou org OFF), on n'affiche RIEN
 *    (cohérent avec le mode additif strict)
 */
export default function ContextSyncIndicator() {
  const [status, setStatus] = useState(null);
  const [pulsing, setPulsing] = useState(false);
  const lastTsRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const r = await api.get("/workspace/context-sync/status");
        if (!mounted) return;
        const newStatus = r.data || {};
        setStatus(newStatus);
        const lastTs = newStatus.last_synced_at;
        if (lastTs && lastTsRef.current && lastTs !== lastTsRef.current) {
          // Nouvelle capture détectée → pulse
          setPulsing(true);
          setTimeout(() => {
            if (mounted) setPulsing(false);
          }, 2500);
        }
        lastTsRef.current = lastTs;
      } catch {
        // Endpoint indisponible → on retire l'indicateur silencieusement
        if (mounted) setStatus({ unavailable: true });
      }
    };
    tick();
    pollRef.current = setInterval(tick, 30000);
    return () => {
      mounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (!status || status.unavailable || status.effective_enabled === false) {
    return null;
  }

  const todayCount = status.today_count || 0;
  const lastTs = status.last_synced_at;
  const lastDisp = lastTs
    ? new Date(lastTs).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <Link
      to="/app/settings/context-sync"
      title={`Noam capte vos modifications en temps réel.\nAujourd'hui : ${todayCount} capture(s) · Dernière : ${lastDisp}`}
      className="relative inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-white/5 transition-colors flex-shrink-0"
      data-testid="context-sync-indicator"
      data-pulsing={pulsing ? "true" : "false"}
    >
      <Activity
        className={`w-3.5 h-3.5 ${pulsing ? "text-emerald-400" : "text-zinc-400"} transition-colors`}
      />
      {pulsing && (
        <span
          className="absolute inset-0 rounded-md ring-2 ring-emerald-400/40 animate-ping"
          aria-hidden="true"
        />
      )}
      {todayCount > 0 && !pulsing && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-[#378ADD] text-[9px] font-semibold text-white flex items-center justify-center"
          data-testid="context-sync-badge-count"
        >
          {todayCount > 99 ? "99+" : todayCount}
        </span>
      )}
    </Link>
  );
}
